import { Component, OnInit, OnDestroy, Input, ElementRef } from '@angular/core';

@Component({
  selector: 'nc-number-ticker',
  templateUrl: './number-ticker.component.html',
  styleUrls: ['./number-ticker.component.less']
})
export class NumberTickerComponent implements OnInit, OnDestroy {
  /** 要显示的数字 */
  private _value: number = 0;
  @Input()
  set ncValue(value: number) {
    this._value = value;
    setTimeout(() => {
      this.start();
    });
  }
  get ncValue(): number {
    return this._value;
  }
  /** 起始数值 */
  @Input() ncStartValue: number = 0;
  /** 开始前的延迟 */
  @Input() ncDelay: number = 0;
  /** 小数位数 */
  @Input() ncDecimalPlaces: number = 0;
  /** 动画持续时间（毫秒） */
  @Input() ncDuration: number = 3000;
  /** 是否在视图可见时才开始动画 */
  private _startOnView: boolean = false;
  @Input()
  set ncStartOnView(val: boolean | string) {
    this._startOnView = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncStartOnView(): boolean {
    return this._startOnView;
  }
  /** 是否只执行一次动画 */
  private _once: boolean = false;
  @Input()
  set ncOnce(val: boolean | string) {
    this._once = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncOnce(): boolean {
    return this._once;
  }

  private observer: IntersectionObserver | null = null;
  private animationFrame: number | null = null;
  private timeout: any = null;

  currentValue: number = 0;
  displayValue: string = '0';

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.clear();
  }

  private start(): void {
    this.clear();
    // 初始值：无论什么方向，都从startValue开始
    this.currentValue = this.ncStartValue;
    this.updateDisplayValue();

    if (!this.ncStartOnView) {
      this.timeout = setTimeout(() => {
        this.startAnimation();
      }, this.ncDelay);
    } else {
      this.setupIntersectionObserver();
    }
  }

  private clear(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.timeout = setTimeout(() => {
          this.startAnimation();
        }, this.ncDelay);

        if (this.ncOnce) {
          if (this.observer) {
            this.observer.disconnect();
          }
        }
      } else if (!this.ncOnce) {
        this.resetAnimation();
      }
    }, { threshold: 0.1 });

    this.observer.observe(this.elementRef.nativeElement);
  }

  private startAnimation(): void {
    this.animate();
  }

  private resetAnimation(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.currentValue = this.ncStartValue;
    this.updateDisplayValue();
  }

  private animate(): void {
    const startTime = performance.now();
    const startVal = this.currentValue;
    // 目标值：无论什么方向，都动画到value
    const targetVal = this.ncValue;
    const difference = targetVal - startVal;

    const animateStep = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.ncDuration, 1);

      // 使用缓动函数（ease-out）
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      this.currentValue = startVal + (difference * easedProgress);
      this.updateDisplayValue();

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animateStep);
      }
    };

    this.animationFrame = requestAnimationFrame(animateStep);
  }

  private updateDisplayValue(): void {
    this.displayValue = this.currentValue.toFixed(this.ncDecimalPlaces);
  }
}
